/**
 * ホスト済み MCP サーバー（Streamable HTTP）への接続情報。
 *
 * MCP には stdio 版とリモート HTTP 版があるが、利用者が自分で clone / build
 * せずに登録できるのはリモート HTTP 版だけなので、画面に出す手順はこちらに限る。
 *
 * 本番用のリモート MCP（mcp.tasky-ai.com）はまだ存在しない。存在しない URL の
 * 手順を出すと「書いてある通りにやったのに繋がらない」になるため、本番では
 * null を返して接続手順そのものを出さない。本番に立てたら
 * REACT_APP_MCP_URL を設定するか、ここの分岐を更新すること。
 */
import { isProductionEnvironment } from './environment';

export type McpConnection = {
  /** MCP クライアントに登録するエンドポイント URL。 */
  url: string;
  /** `claude mcp add <name>` で使うサーバー名。環境が混ざらないよう環境別にする。 */
  serverName: string;
};

const STAGING_CONNECTION: McpConnection = {
  url: 'https://stage-mcp.tasky-ai.com/mcp',
  serverName: 'tasky-staging',
};

const LOCAL_CONNECTION: McpConnection = {
  url: 'http://localhost:3100/mcp',
  serverName: 'tasky-local',
};

export const getMcpConnection = (): McpConnection | null => {
  const configured = process.env.REACT_APP_MCP_URL?.trim();
  if (configured) {
    return { url: configured, serverName: process.env.REACT_APP_MCP_SERVER_NAME?.trim() || 'tasky' };
  }

  if (isProductionEnvironment()) {
    return null;
  }

  // 判定に外れた環境は LOCAL に倒さない。staging の API ホストは
  // secrets.STAGING_EC2_HOST 由来（現在は stage.api.tasky-ai.com）で、値が
  // 変わればこの判定はすり抜ける。そのとき localhost を案内すると、画面は
  // 正常に見えたまま誰にも繋がらない手順を配ることになる。分からないときは
  // 手順を出さない。
  const apiUrl = process.env.REACT_APP_API_URL || '';
  if (apiUrl.includes('stage.')) {
    return STAGING_CONNECTION;
  }
  if (!apiUrl || apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1')) {
    return LOCAL_CONNECTION;
  }

  return null;
};

export type McpClientId = 'claude' | 'codex' | 'gemini' | 'grok';

export type McpClientGuide = {
  id: McpClientId;
  /** タブに出す表示名。 */
  label: string;
  /** snippet をどこに置くかの説明。 */
  target: string;
  snippet: string;
  /** 登録できたことを確かめる手順。 */
  verify: string;
  /** そのクライアント固有の注意。無ければ出さない。 */
  note?: string;
};

const TOKEN_PLACEHOLDER = '<発行したトークン>';

/**
 * クライアントごとの登録手順。すべて実機で挙動を確認した形にしてある。
 *
 * Codex だけ他と違い `experimental_use_rmcp_client` が要る。これが無いと
 * Codex は streamable HTTP ではなく旧 SSE 経路で繋ぎにいき、http_headers を
 * 一切送らないため必ず 401 になる。しかも `codex mcp list` はその状態でも
 * 「enabled / Bearer token」と表示するので、設定を見ても気づけない。
 */
export const buildClientGuides = (
  connection: McpConnection,
  token: string
): McpClientGuide[] => {
  const bearer = token || TOKEN_PLACEHOLDER;
  const { url, serverName } = connection;

  return [
    {
      id: 'claude',
      label: 'Claude Code',
      target: 'ターミナルで実行',
      snippet: [
        `claude mcp add ${serverName} --transport http \\`,
        `  ${url} \\`,
        `  --header "Authorization: Bearer ${bearer}"`,
      ].join('\n'),
      verify:
        'Claude に「Tasky の接続を確認して」と頼み、tasky_health_check が自分のアカウントを返せば完了です。',
      note: '/mcp の Connected 表示だけでは確認になりません。トークンが誤っていても接続済みと表示され、最初にツールを使ったときに認証エラーになります。',
    },
    {
      id: 'codex',
      label: 'Codex',
      target: '~/.codex/config.toml に追記',
      snippet: [
        'experimental_use_rmcp_client = true',
        '',
        `[mcp_servers.${serverName}]`,
        `url = "${url}"`,
        '',
        `[mcp_servers.${serverName}.http_headers]`,
        `Authorization = "Bearer ${bearer}"`,
      ].join('\n'),
      verify: 'codex mcp list に表示され、Codex からツールを呼べれば完了です。',
      note: '1行目の experimental_use_rmcp_client は必須です。これが無いと Codex は認証ヘッダーを送らない旧方式で接続し、必ず認証エラーになります（codex mcp list の表示は正常のままなので気づけません）。',
    },
    {
      id: 'gemini',
      label: 'Gemini CLI',
      target: 'ターミナルで実行',
      snippet: [
        `gemini mcp add ${serverName} ${url} \\`,
        '  --transport http \\',
        `  --header "Authorization: Bearer ${bearer}" \\`,
        '  --scope user',
      ].join('\n'),
      verify: 'gemini mcp list に表示されれば完了です。',
      note: '--scope user を外すと、実行したディレクトリでしか使えない設定になります。',
    },
    {
      id: 'grok',
      label: 'Grok',
      target: 'ターミナルで実行',
      snippet: [
        `grok mcp add ${serverName} ${url} \\`,
        '  --transport http \\',
        `  --header "Authorization: Bearer ${bearer}"`,
      ].join('\n'),
      verify: 'grok mcp doctor を実行し、handshake OK とツール数が出れば完了です。',
    },
  ];
};
